/**
 * 📦 Export Template Modal Component
 * Reusable modal for exporting templates and Excel files
 * Can be used with any Excel data across the application
 */

import React, { useState } from "react";
import {
  X,
  Download,
  FileText,
  CheckCircle,
  AlertCircle,
} from "lucide-react";

const fontMap = {
  xs: { subtitle: "0.875rem", body: "0.75rem", small: "0.6875rem", tiny: "0.625rem" },
  sm: { subtitle: "1rem", body: "0.875rem", small: "0.75rem", tiny: "0.6875rem" },
  md: { subtitle: "1.125rem", body: "0.9375rem", small: "0.8125rem", tiny: "0.75rem" },
  lg: { subtitle: "1.25rem", body: "1rem", small: "0.875rem", tiny: "0.8125rem" },
  xl: { subtitle: "1.375rem", body: "1rem", small: "0.875rem", tiny: "0.8125rem" },
  xxl: { subtitle: "1.5rem", body: "1.125rem", small: "1rem", tiny: "0.875rem" },
};

export const ExportTemplateModal = ({
  isOpen = false,
  onClose = () => {},
  fileName = "template.xlsx",
  fileContent = null, // Blob or File object
  title = "Export Template",
  description = "Download the employee template for bulk import operations",
  successMessage = "Template exported successfully!",
  theme = "light",
  language = "en",
  screenSize = "md",
  isLoading = false,
  onExport = null, // Optional callback for custom export logic
}) => {
  const fonts = fontMap[screenSize] || fontMap.lg;
  const isDark = theme === "dark";
  const [exportStatus, setExportStatus] = useState(null); // 'success', 'error', or null

  if (!isOpen) return null;

  const handleExport = async () => {
    try {
      setExportStatus(null);
      let blobToDownload = null;

      // If custom onExport callback provided, use it
      if (onExport && typeof onExport === "function") {
        const result = await onExport();
        blobToDownload = result instanceof Blob ? result : fileContent;
      } else {
        // Otherwise, use the fileContent
        blobToDownload = fileContent;
      }

      // Validate we have a blob
      if (!blobToDownload) {
        console.error("No file content available for export");
        setExportStatus("error");
        return;
      }

      // Create a blob if it's not already one
      const blob = blobToDownload instanceof Blob ? blobToDownload : new Blob([blobToDownload]);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      link.style.display = "none";
      document.body.appendChild(link);
      
      // Trigger download
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      setExportStatus("success");
      setTimeout(() => {
        onClose();
        setExportStatus(null);
      }, 1500);
    } catch (error) {
      console.error("Export error:", error);
      setExportStatus("error");
    }
  };

  const colors = {
    bg: isDark ? "#0f172a" : "#ffffff",
    surface: isDark ? "#1e293b" : "#f8fafc",
    border: isDark ? "rgba(148,163,184,0.15)" : "rgba(15,23,42,0.08)",
    text: isDark ? "#f1f5f9" : "#0f172a",
    subtext: isDark ? "#94a3b8" : "#64748b",
    muted: isDark ? "#475569" : "#cbd5e1",
  };

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(0,0,0,0.6)",
          backdropFilter: "blur(4px)",
          zIndex: 9999,
          animation: "fadeIn 300ms ease-out",
        }}
        onClick={onClose}
      />

      {/* Modal Container */}
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 10000,
          width: screenSize === "xs" ? "100%" : "90%",
          maxWidth: 500,
          background: isDark
            ? "linear-gradient(135deg, #0f172a 0%, #1a2a3a 100%)"
            : "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
          color: isDark ? "#e6eef8" : "#0f172a",
          borderRadius: 16,
          boxShadow: isDark
            ? "0 25px 60px rgba(0,0,0,0.8), 0 0 1px rgba(255,255,255,0.1)"
            : "0 20px 50px rgba(0,0,0,0.12), 0 0 1px rgba(0,0,0,0.04)",
          border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(148,163,184,0.15)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          animation: "slideUp 400ms cubic-bezier(0.34, 1.56, 0.64, 1)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: screenSize === "xs" ? "1.5rem 1rem 1rem" : "2rem 2rem 1.5rem",
            borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
            position: "relative",
          }}
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            style={{
              position: "absolute",
              top: screenSize === "xs" ? "1rem" : "1.5rem",
              right: screenSize === "xs" ? "1rem" : "1.5rem",
              width: 40,
              height: 40,
              borderRadius: 10,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              background: isDark ? "rgba(13,18,28,0.15)" : "rgba(255,255,255,0.92)",
              border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
              cursor: "pointer",
              transition: "all 200ms ease",
              color: isDark ? "#94a3b8" : "#475569",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)";
              e.currentTarget.style.transform = "scale(1.05)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = isDark ? "rgba(13,18,28,0.15)" : "rgba(255,255,255,0.92)";
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            <X size={20} />
          </button>

          {/* Title */}
          <h2
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              margin: 0,
              fontSize: fonts.subtitle,
              fontWeight: "700",
              background: isDark
                ? "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)"
                : "linear-gradient(135deg, #080808 0%, #080808 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              paddingRight: "2.5rem",
            }}
          >
            <FileText size={24} style={{ color: isDark ? "#ffffff" : "#080808" }} />
            <span>{title}</span>
          </h2>
        </div>

        {/* Content */}
        <div
          style={{
            flex: 1,
            padding: screenSize === "xs" ? "1.5rem 1rem" : "2rem",
            display: "flex",
            flexDirection: "column",
            gap: "1.5rem",
            overflowY: "auto",
            scrollbarWidth: "thin",
            scrollbarColor: isDark ? "#334155 transparent" : "#cbd5e1 transparent",
          }}
        >
          {/* Icon Container */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
            }}
          >
            <div
              style={{
                width: 80,
                height: 80,
                borderRadius: 16,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: isDark
                  ? "linear-gradient(135deg, rgba(96,165,250,0.2) 0%, rgba(167,139,250,0.2) 100%)"
                  : "linear-gradient(135deg, rgba(37,99,235,0.1) 0%, rgba(124,58,237,0.1) 100%)",
                border: isDark
                  ? "2px solid rgba(96,165,250,0.3)"
                  : "2px solid rgba(37,99,235,0.2)",
              }}
            >
              <Download
                size={40}
                style={{
                  color: isDark ? "#60a5fa" : "#2e7cba",
                }}
              />
            </div>
          </div>

          {/* Description */}
          <div style={{ textAlign: "center" }}>
            <p
              style={{
                margin: 0,
                fontSize: fonts.body,
                color: colors.subtext,
                lineHeight: "1.6",
              }}
            >
              {description}
            </p>
          </div>

          {/* Status Messages */}
          {exportStatus === "success" && (
            <div
              style={{
                padding: screenSize === "xs" ? "0.75rem 1rem" : "1rem 1.25rem",
                borderRadius: 10,
                background: isDark
                  ? "rgba(34, 197, 94, 0.1)"
                  : "rgba(34, 197, 94, 0.08)",
                border: isDark
                  ? "1px solid rgba(34, 197, 94, 0.3)"
                  : "1px solid rgba(34, 197, 94, 0.2)",
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
              }}
            >
              <CheckCircle size={20} style={{ color: "#22c55e", flexShrink: 0 }} />
              <span
                style={{
                  fontSize: fonts.body,
                  color: isDark ? "#86efac" : "#16a34a",
                  fontWeight: 600,
                }}
              >
                {successMessage}
              </span>
            </div>
          )}

          {exportStatus === "error" && (
            <div
              style={{
                padding: screenSize === "xs" ? "0.75rem 1rem" : "1rem 1.25rem",
                borderRadius: 10,
                background: isDark
                  ? "rgba(239, 68, 68, 0.1)"
                  : "rgba(239, 68, 68, 0.08)",
                border: isDark
                  ? "1px solid rgba(239, 68, 68, 0.3)"
                  : "1px solid rgba(239, 68, 68, 0.2)",
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
              }}
            >
              <AlertCircle size={20} style={{ color: "#ef4444", flexShrink: 0 }} />
              <span
                style={{
                  fontSize: fonts.body,
                  color: isDark ? "#fca5a5" : "#dc2626",
                  fontWeight: 600,
                }}
              >
                {language === "ar" ? "فشل التحميل" : "Export failed"}
              </span>
            </div>
          )}

          {/* File Info */}
          <div
            style={{
              padding: screenSize === "xs" ? "1rem" : "1.25rem",
              borderRadius: 12,
              background: isDark ? "rgba(30,41,59,0.5)" : "rgba(226,232,240,0.4)",
              border: isDark
                ? "1px solid rgba(148,163,184,0.2)"
                : "1px solid rgba(100,116,139,0.15)",
            }}
          >
            <div style={{ fontSize: fonts.small, color: colors.muted, marginBottom: "0.5rem" }}>
              {language === "ar" ? "اسم الملف:" : "File Name:"}
            </div>
            <div
              style={{
                fontSize: fonts.body,
                fontWeight: 600,
                color: colors.text,
                wordBreak: "break-all",
              }}
            >
              {fileName}
            </div>
          </div>

        </div>

        {/* Footer */}
        <div
          style={{
            padding: screenSize === "xs" ? "1rem" : "1.5rem 2rem",
            borderTop: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
            display: "flex",
            gap: screenSize === "xs" ? "0.75rem" : "1rem",
            justifyContent: "flex-end",
          }}
        >
          <button
            onClick={onClose}
            disabled={isLoading || exportStatus === "success"}
            style={{
              padding: screenSize === "xs" ? "0.625rem 1rem" : "0.75rem 1.5rem",
              borderRadius: 10,
              border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)",
              background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)",
              color: isDark ? "#cbd5e1" : "#64748b",
              fontSize: fonts.body,
              fontWeight: 600,
              cursor: isLoading || exportStatus === "success" ? "not-allowed" : "pointer",
              transition: "all 200ms ease",
              opacity: isLoading || exportStatus === "success" ? 0.6 : 1,
            }}
            onMouseEnter={(e) => {
              if (!isLoading && exportStatus !== "success") {
                e.currentTarget.style.background = isDark
                  ? "rgba(255,255,255,0.1)"
                  : "rgba(0,0,0,0.08)";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = isDark
                ? "rgba(255,255,255,0.05)"
                : "rgba(0,0,0,0.05)";
            }}
          >
            {language === "ar" ? "إغلاق" : "Close"}
          </button>

          <button
            onClick={handleExport}
            disabled={isLoading || exportStatus === "success" || (!fileContent && !onExport)}
            style={{
              padding: screenSize === "xs" ? "0.625rem 1rem" : "0.75rem 1.5rem",
              borderRadius: 10,
              border: "none",
              background: isDark
                ? "linear-gradient(135deg, #2e7cba 0%, #2e7cba 100%)"
                : "linear-gradient(135deg, #2e7cba 0%, #2e7cba 100%)",
              color: "#fff",
              fontSize: fonts.body,
              fontWeight: 600,
              cursor: isLoading || exportStatus === "success" || (!fileContent && !onExport) ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              boxShadow: isDark
                ? "0 4px 16px rgba(59,130,246,0.25)"
                : "0 4px 16px rgba(37,99,235,0.15)",
              opacity: isLoading || exportStatus === "success" || (!fileContent && !onExport) ? 0.7 : 1,
              transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
              whiteSpace: "nowrap",
            }}
            onMouseEnter={(e) => {
              if (!isLoading && exportStatus !== "success" && (fileContent || onExport)) {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = isDark
                  ? "0 6px 24px rgba(59,130,246,0.35)"
                  : "0 6px 24px rgba(37,99,235,0.25)";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = isDark
                ? "0 4px 16px rgba(59,130,246,0.25)"
                : "0 4px 16px rgba(37,99,235,0.15)";
            }}
          >
            <Download size={18} />
            {isLoading
              ? language === "ar"
                ? "جاري التحميل..."
                : "Exporting..."
              : language === "ar"
                ? "تحميل الملف"
                : "Export File"}
          </button>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translate(-50%, -48%);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%);
          }
        }
      `}</style>
    </>
  );
};

export default ExportTemplateModal;
